import htmlToPdfmake from "html-to-pdfmake";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import defaultBrandLogo from "../../assets/logo1.png";
import { buildInvoiceEmailHtml } from "../../utilis/invoiceEmailHtmlHelpers";
import {
  resolveInvoiceLogoUrl,
  resolveInvoicePreviewLogoUrl,
} from "../../utilis/invoiceSettingHelpers";

const ROBOTO_FILES = [
  "Roboto-Regular.ttf",
  "Roboto-Medium.ttf",
  "Roboto-Italic.ttf",
  "Roboto-MediumItalic.ttf",
];

const ROBOTO_FONT = {
  Roboto: {
    normal: "Roboto-Regular.ttf",
    bold: "Roboto-Medium.ttf",
    italics: "Roboto-Italic.ttf",
    bolditalics: "Roboto-MediumItalic.ttf",
  },
};

let pdfMakeVfs = null;

function resolvePdfMakeVfs(fontsModule) {
  if (!fontsModule) return null;
  if (fontsModule["Roboto-Regular.ttf"]) return fontsModule;
  if (fontsModule.default?.["Roboto-Regular.ttf"]) return fontsModule.default;
  if (fontsModule.pdfMake?.vfs?.["Roboto-Regular.ttf"]) {
    return fontsModule.pdfMake.vfs;
  }
  return null;
}

function getRobotoVfs() {
  if (pdfMakeVfs) return pdfMakeVfs;
  const raw = resolvePdfMakeVfs(pdfFonts);
  if (!raw?.["Roboto-Regular.ttf"]) {
    throw new Error("PDF fonts failed to load (Roboto vfs missing).");
  }
  pdfMakeVfs = {};
  ROBOTO_FILES.forEach((name) => {
    if (raw[name]) pdfMakeVfs[name] = raw[name];
  });
  return pdfMakeVfs;
}

const IMAGE_FETCH_TIMEOUT_MS = 8000;

/** Vertical gaps between invoice PDF sections (pt). Aligned with preview body/parties/footer spacing. */
const PDF_GAP_AFTER_HEADER_PT = 21;
const PDF_GAP_AFTER_PARTIES_PT = 30;
const PDF_GAP_AFTER_LINES_PT = 0;
const PDF_GAP_AFTER_TOTALS_PT = 14;
const PDF_BODY_HORIZONTAL_MARGIN_PT = 40;
/** Narrow right-aligned totals panel (preview max-width 320px; panel fits label + value). */
const INVOICE_TOTALS_PANEL_WIDTH_PT = 272;
const PDF_PAGE_BOTTOM_MARGIN_PT = 28;
/** Legacy preview spacing reference (PDF uses flush top margin; see INVOICE_PAGE_MARGINS). */
const PDF_PAGE_TOP_MARGIN_PT = 36;

const INVOICE_HEADER_BAND_FILL = "#ebf7ed";

/** No top page margin — header band sits flush; body blocks carry horizontal inset. Avoids negative-margin blank pages. */
const INVOICE_PAGE_MARGINS = [0, 0, 0, PDF_PAGE_BOTTOM_MARGIN_PT];
const INVOICE_HEADER_TOP_OFFSET_MARGIN = [0, 0, 0, 0];

function invoiceTotalsRowLabel(row) {
  if (!Array.isArray(row) || !row[0]) return "";
  const cell = row[0];
  if (typeof cell.text === "string") return cell.text;
  if (Array.isArray(cell.text)) {
    return cell.text
      .map((part) =>
        typeof part === "string" ? part : String(part?.text ?? ""),
      )
      .join("");
  }
  if (Array.isArray(cell.stack)) {
    return cell.stack
      .map((part) =>
        typeof part?.text === "string" ? part.text : JSON.stringify(part),
      )
      .join(" ");
  }
  return JSON.stringify(cell);
}

function invoiceTotalsRowNeedsTopRule(rowIndex, body) {
  if (!Array.isArray(body) || rowIndex < 1 || rowIndex >= body.length) {
    return false;
  }
  const label = invoiceTotalsRowLabel(body[rowIndex]).toUpperCase();
  if (label.includes("AMOUNT DUE")) {
    const prev = invoiceTotalsRowLabel(body[rowIndex - 1]).toUpperCase();
    return prev.includes("LESS") || prev.includes("PAID");
  }
  return label.includes("TOTAL") && !label.includes("AMOUNT DUE");
}

const INVOICE_TABLE_LAYOUTS = {
  invoiceHeader: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 40,
    paddingRight: () => 40,
    paddingTop: () => HEADER_BAND_PADDING_TOP_PT,
    paddingBottom: () => 0,
    fillColor: () => INVOICE_HEADER_BAND_FILL,
  },
  invoiceHeaderBand: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => HEADER_BAND_PADDING_TOP_PT,
    paddingBottom: () => 0,
    fillColor: () => INVOICE_HEADER_BAND_FILL,
  },
  invoiceLines: {
    hLineWidth: (i, node) => {
      if (i === 0) return 0;
      if (i === 1) return 0.7;
      if (i === node.table.body.length) return 0;
      return 0.4;
    },
    vLineWidth: () => 0,
    hLineColor: (i) => (i === 1 ? "#d1d5db" : "#f0f0f0"),
    paddingLeft: () => 8,
    paddingRight: () => 8,
    paddingTop: () => 8,
    paddingBottom: () => 8,
  },
  invoiceTotals: {
    hLineWidth: (lineIndex, node) => {
      const body = node?.table?.body;
      if (!Array.isArray(body)) return 0;
      return invoiceTotalsRowNeedsTopRule(lineIndex, body) ? 0.8 : 0;
    },
    vLineWidth: () => 0,
    hLineColor: () => "#111827",
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: (rowIndex, node) => {
      const body = node?.table?.body;
      if (
        Array.isArray(body) &&
        invoiceTotalsRowNeedsTopRule(rowIndex, body)
      ) {
        return 10;
      }
      return 4;
    },
    paddingBottom: () => 4,
  },
};

function pdfMakeGetBlob(documentDefinition) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(
        new Error(
          "PDF generation stalled. The invoice was converted to text but pdfMake did not finish writing the file.",
        ),
      );
    }, 20000);

    try {
      const vfs = getRobotoVfs();
      pdfMake.vfs = vfs;
      pdfMake.fonts = ROBOTO_FONT;

      const pdf = pdfMake.createPdf(
        documentDefinition,
        INVOICE_TABLE_LAYOUTS,
        ROBOTO_FONT,
        vfs,
      );

      const finish = (result, isBlob) => {
        window.clearTimeout(timeoutId);
        if (!result) {
          reject(new Error("PDF generation returned an empty result."));
          return;
        }
        resolve(
          isBlob
            ? result
            : new Blob([result], { type: "application/pdf" }),
        );
      };

      if (typeof pdf.getBuffer === "function") {
        pdf.getBuffer((buffer) => finish(buffer, false));
        return;
      }
      pdf.getBlob((blob) => finish(blob, true));
    } catch (error) {
      window.clearTimeout(timeoutId);
      reject(error);
    }
  });
}

async function fetchImageAsDataUrl(src) {
  const url = String(src || "").trim();
  if (!url || url.startsWith("data:")) return url;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    IMAGE_FETCH_TIMEOUT_MS,
  );

  try {
    const response = await fetch(url, {
      mode: "cors",
      credentials: "include",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to load image for PDF (${response.status}): ${url}`);
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error(`Failed to encode image: ${url}`));
      reader.readAsDataURL(blob);
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function resolveInvoicePdfLogoDataUrl(draft) {
  const candidates = [
    resolveInvoiceLogoUrl(draft?.invoiceSetting?.c_logo),
    resolveInvoicePreviewLogoUrl({ invoiceSetting: draft?.invoiceSetting }),
    defaultBrandLogo,
  ].filter((url, index, list) => url && list.indexOf(url) === index);

  for (const url of candidates) {
    try {
      return await fetchImageAsDataUrl(url);
    } catch (error) {
      console.warn("invoicePdfVector: logo fetch failed, trying next", url, error);
    }
  }
  return null;
}

function prepareHtmlForPdfMake(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("style, script, link").forEach((node) => node.remove());
  doc.querySelectorAll("h1").forEach((heading) => {
    const replacement = doc.createElement("div");
    replacement.setAttribute(
      "style",
      "margin:0;font-size:20px;font-weight:700;letter-spacing:0.02em;text-transform:uppercase;color:#111827;line-height:1.1;",
    );
    replacement.innerHTML = heading.innerHTML;
    heading.replaceWith(replacement);
  });
  doc.querySelectorAll("[style]").forEach((el) => {
    const style = String(el.getAttribute("style") || "")
      .replace(/font-family\s*:[^;]+;?/gi, "")
      .replace(/(?:^|;)\s*font\s*:[^;]+/gi, "")
      .replace(/display\s*:\s*(flex|grid)[^;]*;?/gi, "");
    if (style.trim()) el.setAttribute("style", style);
    else el.removeAttribute("style");
  });
  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") || "";
    if (!src.startsWith("data:")) img.remove();
  });
  const sheet = doc.querySelector(".sheet") || doc.body;
  if (sheet) {
    const walker = doc.createTreeWalker(sheet, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach((node) => {
      if (!String(node.textContent || "").replace(/\u00a0/g, " ").trim()) {
        node.remove();
      }
    });
  }
  const fragment = sheet ? sheet.innerHTML || sheet.outerHTML : "";
  return fragment.replace(/>\s+</g, "><").trim();
}

export async function inlineImagesInHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const images = Array.from(doc.querySelectorAll("img[src]"));

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return;
      try {
        img.setAttribute("src", await fetchImageAsDataUrl(src));
      } catch (error) {
        console.warn("invoicePdfVector: dropping image for PDF", src, error);
        img.remove();
      }
    }),
  );

  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
}

function sanitizePdfMakeNode(node) {
  if (node == null) return node;
  if (Array.isArray(node)) {
    return node.map(sanitizePdfMakeNode).filter((item) => item != null);
  }
  if (typeof node !== "object") return node;

  const next = { ...node };

  if (next.font && next.font !== "Roboto") {
    delete next.font;
  }

  [
    "display",
    "position",
    "overflow",
    "float",
    "boxSizing",
    "visibility",
    "whiteSpace",
    "objectFit",
    "objectPosition",
    "pageBreak",
    "pageBreakBefore",
    "pageBreakAfter",
    "pageOrientation",
    "unbreakable",
    "noWrap",
  ].forEach((key) => {
    delete next[key];
  });

  if (next.dontBreakRows) {
    delete next.dontBreakRows;
  }

  if (next.marginBottom === 5) {
    delete next.marginBottom;
  }

  if (typeof next.image === "string") {
    if (
      !next.image.startsWith("data:") &&
      !next.image.startsWith("http") &&
      !next.image.startsWith("img_ref_")
    ) {
      delete next.image;
      if (!next.text && !next.stack && !next.table && !next.columns) {
        return null;
      }
    } else {
      next.fit = HEADER_LOGO_FIT;
      delete next.width;
      delete next.height;
    }
  }

  if (next.stack) next.stack = sanitizePdfMakeNode(next.stack);
  if (next.columns) next.columns = sanitizePdfMakeNode(next.columns);
  if (next.ul) next.ul = sanitizePdfMakeNode(next.ul);
  if (next.ol) next.ol = sanitizePdfMakeNode(next.ol);
  if (Array.isArray(next.text)) next.text = sanitizePdfMakeNode(next.text);
  const isHeaderLayout =
    next.layout === "invoiceHeader" || next.layout === "invoiceHeaderBand";
  if (!isHeaderLayout) {
    delete next.height;
    delete next.minHeight;
  }

  if (next.table?.body) {
    next.table = {
      ...next.table,
      body: next.table.body.map((row) =>
        Array.isArray(row)
          ? row.map((cell) => {
              const cleaned = sanitizePdfMakeNode(cell);
              if (
                cleaned &&
                typeof cleaned === "object" &&
                next.layout !== "invoiceTotals"
              ) {
                delete cleaned.border;
                delete cleaned.borderColor;
              }
              return cleaned;
            })
          : sanitizePdfMakeNode(row),
      ),
    };
    const colCount = Array.isArray(next.table.body[0])
      ? next.table.body[0].length
      : 0;
    if (!next.layout) {
      next.layout = colCount >= 5 ? "invoiceLines" : "noBorders";
    }
    next.table.widths = resolveTableWidths(next.layout, colCount, next.table.widths);
    if (next.layout === "invoiceHeader") {
      delete next.table.heights;
    }
  }

  return next;
}

function normalizeWidthValue(value) {
  if (value === "*" || value === "auto") return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : "*";
}

function resolveTableWidths(layout, colCount, existing) {
  if (
    (layout === "invoiceHeader" || layout === "invoiceHeaderBand") &&
    colCount === 2
  ) {
    return ["*", "*"];
  }
  if (layout === "invoiceLines") {
    if (colCount === 6) return ["*", 36, 54, 46, 40, 64];
    if (colCount === 5) return ["*", 36, 54, 40, 64];
    return Array(colCount).fill("*");
  }
  if (layout === "invoiceTotals" && colCount === 2) {
    return ["*", "auto"];
  }
  if (
    layout === "noBorders" &&
    colCount === 2 &&
    Array.isArray(existing) &&
    Number(existing[1]) > 0
  ) {
    return ["*", Number(existing[1])];
  }
  if (Array.isArray(existing) && existing.length === colCount) {
    return existing.map(normalizeWidthValue);
  }
  if (colCount === 3) return ["*", "*", "*"];
  if (colCount === 2) return ["*", "*"];
  return Array(Math.max(colCount, 1)).fill("*");
}

function normalizeHtmlToPdfmakeResult(converted) {
  if (!converted) {
    throw new Error("HTML to PDF conversion produced no content.");
  }

  if (converted.content !== undefined) {
    return {
      content: converted.content,
      images: converted.images || {},
    };
  }

  return { content: converted, images: {} };
}

function isInvoiceHeaderBand(item) {
  return (
    item &&
    typeof item === "object" &&
    (item.layout === "invoiceHeader" ||
      item.layout === "invoiceHeaderBand")
  );
}

/** A4 width (pt); accent line matches preview ::after (40pt inset each side). */
const PAGE_WIDTH_PT = 595.28;
const HEADER_SIDE_INSET_PT = 40;
const HEADER_COLUMN_GAP_PT = 12;
/** Matches preview `.invoice-xero-preview__header-band` padding (14px / 12px). */
const HEADER_BAND_PADDING_TOP_PT = 11;
const HEADER_BAND_PADDING_BOTTOM_PT = 9;
const HEADER_BAND_ACCENT_HEIGHT_PT = 2;
const HEADER_ACCENT_INSET_PT = 40;
const HEADER_LOGO_FIT = [178, 28];

function tightenInvoiceHeaderNode(node) {
  if (node == null) return node;
  if (Array.isArray(node)) {
    return node.map(tightenInvoiceHeaderNode);
  }
  if (typeof node !== "object") return node;

  const next = { ...node, margin: [0, 0, 0, 0] };
  delete next.minHeight;
  delete next.height;
  if (next.lineHeight != null) next.lineHeight = 1;
  if (typeof next.image === "string") {
    next.fit = HEADER_LOGO_FIT;
    delete next.width;
    delete next.height;
  }

  if (next.stack) next.stack = tightenInvoiceHeaderNode(next.stack);
  if (next.columns) next.columns = tightenInvoiceHeaderNode(next.columns);
  if (Array.isArray(next.text)) next.text = tightenInvoiceHeaderNode(next.text);

  return next;
}

function applyHeaderContentCellLayout(cell, columnIndex) {
  const tightened = tightenInvoiceHeaderNode(cell);
  if (columnIndex === 0) {
    return {
      ...tightened,
      verticalAlignment: "middle",
      margin: [
        HEADER_SIDE_INSET_PT,
        0,
        HEADER_COLUMN_GAP_PT,
        HEADER_BAND_PADDING_BOTTOM_PT,
      ],
    };
  }
  return {
    ...tightened,
    alignment: "right",
    verticalAlignment: "middle",
    margin: [
      HEADER_COLUMN_GAP_PT,
      0,
      HEADER_SIDE_INSET_PT,
      HEADER_BAND_PADDING_BOTTOM_PT,
    ],
  };
}

function tightenInvoiceHeaderTable(headerTable) {
  if (!headerTable?.table?.body?.[0]) return headerTable;
  const contentRow = headerTable.table.body[0].map((cell, columnIndex) =>
    applyHeaderContentCellLayout(cell, columnIndex),
  );
  return {
    ...headerTable,
    layout: "invoiceHeaderBand",
    margin: [0, 0, 0, 0],
    table: {
      ...headerTable.table,
      widths: ["*", "*"],
      width: PAGE_WIDTH_PT,
      body: [
        contentRow,
        [
          {
            colSpan: 2,
            border: [false, false, false, false],
            fillColor: INVOICE_HEADER_BAND_FILL,
            canvas: [
              {
                type: "line",
                x1: HEADER_ACCENT_INSET_PT,
                y1: HEADER_BAND_ACCENT_HEIGHT_PT,
                x2: PAGE_WIDTH_PT - HEADER_ACCENT_INSET_PT,
                y2: HEADER_BAND_ACCENT_HEIGHT_PT,
                lineWidth: HEADER_BAND_ACCENT_HEIGHT_PT,
                lineColor: "#1a4f9c",
              },
            ],
            margin: [0, 0, 0, 0],
          },
          {},
        ],
      ],
      heights: ["auto", HEADER_BAND_ACCENT_HEIGHT_PT],
    },
  };
}

function wrapInvoiceHeaderWithInsetAccent(headerTable) {
  return tightenInvoiceHeaderTable({
    ...headerTable,
    margin: [0, 0, 0, 0],
  });
}

function compactNestedSectionMargins(node) {
  if (node == null) return node;
  if (Array.isArray(node)) {
    return node.map(compactNestedSectionMargins);
  }
  if (typeof node !== "object") return node;

  const next = { ...node };
  const isHeader =
    next.layout === "invoiceHeader" || next.layout === "invoiceHeaderBand";

  if (next.table && !isHeader) {
    next.margin = [0, 0, 0, 0];
  }
  if (next.stack) next.stack = compactNestedSectionMargins(next.stack);
  if (next.columns) next.columns = compactNestedSectionMargins(next.columns);
  if (Array.isArray(next.text)) next.text = compactNestedSectionMargins(next.text);

  return next;
}

function isStructuralPdfWrapperNode(node) {
  if (!node || typeof node !== "object") return false;
  if (node.table || node.image || node.canvas) return false;
  if (node.layout === "invoiceHeader" || node.layout === "invoiceHeaderBand") {
    return false;
  }
  return Array.isArray(node.stack) && node.stack.length > 0 && node.text == null;
}

function flattenInvoicePdfBlocks(nodes) {
  const items = Array.isArray(nodes) ? nodes : [nodes];
  const flattened = [];
  items.forEach((node) => {
    if (!node) return;
    if (
      typeof node === "object" &&
      !Array.isArray(node) &&
      Object.keys(node).length === 0
    ) {
      return;
    }
    if (isStructuralPdfWrapperNode(node)) {
      flattened.push(...flattenInvoicePdfBlocks(node.stack));
      return;
    }
    flattened.push(node);
  });
  return flattened;
}

function pdfPlainTextIsSignificant(text) {
  return String(text ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, "")
    .length > 0;
}

function nodeHasRenderableContent(node) {
  if (node == null) return false;
  if (typeof node === "string") return pdfPlainTextIsSignificant(node);
  if (typeof node !== "object" || Array.isArray(node)) return false;
  if (node.table?.body?.length) return true;
  if (node.image || node.canvas) return true;
  if (node.text != null) {
    if (typeof node.text === "string") return pdfPlainTextIsSignificant(node.text);
    if (Array.isArray(node.text)) {
      return node.text.some((part) => nodeHasRenderableContent(part));
    }
  }
  if (Array.isArray(node.stack) && node.stack.length > 0) {
    return node.stack.some((child) => nodeHasRenderableContent(child));
  }
  if (Array.isArray(node.columns) && node.columns.length > 0) {
    return node.columns.some((child) => nodeHasRenderableContent(child));
  }
  if (Array.isArray(node.ul) && node.ul.length > 0) return true;
  if (Array.isArray(node.ol) && node.ol.length > 0) return true;
  return false;
}

function isEmptyPdfBlock(item) {
  if (item == null) return true;
  if (typeof item === "object" && !Array.isArray(item) && Object.keys(item).length === 0) {
    return true;
  }
  return !nodeHasRenderableContent(item);
}

function prunePdfContentItems(items) {
  return items.filter((item) => !isEmptyPdfBlock(item));
}

function normalizeFooterTableCell(cell) {
  if (cell == null || typeof cell !== "object") return null;
  const next = { ...cell };
  delete next.border;
  delete next.borderColor;
  if (isEmptyPdfBlock(next)) return null;
  return next;
}

/**
 * Footer as a single table row cannot split in pdfMake; columns let each side paginate independently.
 */
function convertFooterTableToColumns(item) {
  if (!isFooterBlock(item) || !item.table?.body?.length) return item;
  const row = item.table.body[0];
  if (!Array.isArray(row)) return item;

  const columnCells = row
    .map((cell) => normalizeFooterTableCell(cell))
    .filter(Boolean);
  if (!columnCells.length) return null;

  const margin = item.margin || [
    PDF_BODY_HORIZONTAL_MARGIN_PT,
    PDF_GAP_AFTER_TOTALS_PT,
    PDF_BODY_HORIZONTAL_MARGIN_PT,
    0,
  ];

  if (columnCells.length === 1) {
    const only = columnCells[0];
    return {
      ...(only.stack ? { stack: only.stack } : { stack: [only] }),
      margin,
    };
  }

  return {
    columns: columnCells.map((cell) => ({
      width: "*",
      ...(cell.stack ? { stack: cell.stack } : { stack: [cell] }),
    })),
    columnGap: 24,
    margin,
  };
}

function applyInvoiceTotalsPanelGeometry(node) {
  if (node == null || typeof node !== "object") return node;
  if (Array.isArray(node)) {
    return node.map(applyInvoiceTotalsPanelGeometry);
  }

  let next = { ...node };
  if (next.table?.layout === "invoiceTotals") {
    next.table = {
      ...next.table,
      width: INVOICE_TOTALS_PANEL_WIDTH_PT,
      widths: ["*", "auto"],
    };
  }
  if (isTotalsAlignmentTable(next) && tableColumnCount(next) === 2) {
    next.table = {
      ...next.table,
      widths: ["*", INVOICE_TOTALS_PANEL_WIDTH_PT],
    };
  }
  if (next.table?.body) {
    next.table = {
      ...next.table,
      body: next.table.body.map((row) =>
        Array.isArray(row)
          ? row.map((cell) => applyInvoiceTotalsPanelGeometry(cell))
          : applyInvoiceTotalsPanelGeometry(row),
      ),
    };
  }
  if (next.stack) {
    next.stack = applyInvoiceTotalsPanelGeometry(next.stack);
  }
  if (next.columns) {
    next.columns = applyInvoiceTotalsPanelGeometry(next.columns);
  }
  return next;
}

function optimizeFooterBlocksForPdf(items) {
  return items
    .map((item) => {
      if (!isFooterBlock(item) || !item.table) return item;
      return convertFooterTableToColumns(item);
    })
    .filter((item) => item != null && !isEmptyPdfBlock(item));
}

function finalizePdfContentItems(items) {
  let list = Array.isArray(items) ? [...items] : [items];
  list = prunePdfContentItems(list);

  while (list.length > 0) {
    const last = list[list.length - 1];
    if (isEmptyPdfBlock(last)) {
      list.pop();
      continue;
    }
    if (
      typeof last === "object" &&
      Array.isArray(last.stack) &&
      last.stack.length > 0
    ) {
      const compactStack = prunePdfContentItems(last.stack);
      if (!compactStack.length) {
        list.pop();
        continue;
      }
      if (compactStack.length !== last.stack.length) {
        list[list.length - 1] = { ...last, stack: compactStack };
      }
    }
    break;
  }

  if (list.length > 0) {
    const lastIndex = list.length - 1;
    const last = list[lastIndex];
    if (last && typeof last === "object") {
      list[lastIndex] = { ...last };
      delete list[lastIndex].pageBreak;
      delete list[lastIndex].pageBreakBefore;
      delete list[lastIndex].pageBreakAfter;
    }
  }

  return list;
}

function normalizePdfContentRoot(content) {
  const items = Array.isArray(content) ? content : [content];
  if (items.length === 1 && isStructuralPdfWrapperNode(items[0])) {
    return flattenInvoicePdfBlocks(items[0].stack);
  }
  return flattenInvoicePdfBlocks(items);
}

function collapseTopSpacingOnFirstBodyBlock(node) {
  if (node == null) return node;
  if (Array.isArray(node)) {
    if (node.length > 0) {
      const [first, ...rest] = node;
      return [collapseTopSpacingOnFirstBodyBlock(first), ...rest];
    }
    return node;
  }
  if (typeof node !== "object") return node;

  const next = { ...node, margin: [0, 0, 0, 0] };
  if (next.stack?.length) {
    const [firstChild, ...restChildren] = next.stack;
    next.stack = [
      collapseTopSpacingOnFirstBodyBlock(firstChild),
      ...restChildren.map((child) => compactNestedSectionMargins(child)),
    ];
  }
  if (next.columns) next.columns = collapseTopSpacingOnFirstBodyBlock(next.columns);
  if (Array.isArray(next.text)) next.text = collapseTopSpacingOnFirstBodyBlock(next.text);

  return next;
}

function tableColumnCount(item) {
  const row = item?.table?.body?.[0];
  return Array.isArray(row) ? row.length : 0;
}

function isPartiesTable(item) {
  return item?.layout === "noBorders" && tableColumnCount(item) === 3;
}

function isLinesTable(item) {
  return item?.layout === "invoiceLines";
}

function cellHasInvoiceTotalsTable(cell) {
  if (!cell || typeof cell !== "object") return false;
  if (cell.table?.layout === "invoiceTotals") return true;
  if (Array.isArray(cell.stack)) {
    return cell.stack.some((node) => cellHasInvoiceTotalsTable(node));
  }
  return false;
}

function isTotalsAlignmentTable(item) {
  if (item?.table?.layout === "invoiceTotals") return true;
  if (!item?.table?.body?.[0]) return false;
  const row = item.table.body[0];
  if (!Array.isArray(row)) return false;
  if (row.length === 1) return cellHasInvoiceTotalsTable(row[0]);
  if (row.length !== 2) return false;
  return cellHasInvoiceTotalsTable(row[1]);
}

function isFooterBlock(item) {
  const serialized = JSON.stringify(item || {});
  return (
    serialized.includes("Payment Advice") ||
    serialized.includes("Payment Terms and Conditions")
  );
}

function resolvePdfBlockMarginTop(items, index, headerIndex) {
  if (index <= headerIndex) return 0;
  if (index === headerIndex + 1) return PDF_GAP_AFTER_HEADER_PT;

  const prev = items[index - 1];
  const current = items[index];
  if (isLinesTable(prev) && isTotalsAlignmentTable(current)) {
    return PDF_GAP_AFTER_LINES_PT;
  }
  if (isTotalsAlignmentTable(prev) && isFooterBlock(current)) {
    return PDF_GAP_AFTER_TOTALS_PT;
  }
  if (isPartiesTable(prev) && isLinesTable(current)) {
    return PDF_GAP_AFTER_PARTIES_PT;
  }
  return 0;
}

function applyPreviewGeometry(content) {
  const items = Array.isArray(content) ? content : [content];
  const headerIndex = items.findIndex((item) => isInvoiceHeaderBand(item));

  return items.map((item, index) => {
    if (!item || typeof item !== "object") return item;
    if (item.layout === "invoiceHeader") {
      const wrapped = wrapInvoiceHeaderWithInsetAccent(item);
      return {
        ...wrapped,
        margin: INVOICE_HEADER_TOP_OFFSET_MARGIN,
        pageBreak: undefined,
      };
    }
    if (item.layout === "invoiceHeaderBand") {
      return {
        ...item,
        margin: INVOICE_HEADER_TOP_OFFSET_MARGIN,
        pageBreak: undefined,
      };
    }
    const isFirstBody = headerIndex >= 0 ? index === headerIndex + 1 : index === 0;
    const compacted = isFirstBody
      ? collapseTopSpacingOnFirstBodyBlock(item)
      : compactNestedSectionMargins(item);
    return {
      ...compacted,
      pageBreak: undefined,
      margin: [
        PDF_BODY_HORIZONTAL_MARGIN_PT,
        resolvePdfBlockMarginTop(items, index, headerIndex),
        PDF_BODY_HORIZONTAL_MARGIN_PT,
        0,
      ],
    };
  });
}

function ensurePdfContent(content) {
  const sanitized = sanitizePdfMakeNode(content);
  if (Array.isArray(sanitized) && sanitized.length > 0) {
    const normalized = normalizePdfContentRoot(sanitized);
    const pruned = prunePdfContentItems(normalized);
    if (!pruned.length) {
      throw new Error("HTML to PDF conversion produced empty content.");
    }
    return finalizePdfContentItems(
      prunePdfContentItems(
        optimizeFooterBlocksForPdf(
          applyInvoiceTotalsPanelGeometry(applyPreviewGeometry(pruned)),
        ),
      ),
    );
  }
  if (sanitized && typeof sanitized === "object" && !Array.isArray(sanitized)) {
    const normalized = normalizePdfContentRoot([sanitized]);
    const pruned = prunePdfContentItems(normalized);
    if (!pruned.length) {
      throw new Error("HTML to PDF conversion produced empty content.");
    }
    return finalizePdfContentItems(
      prunePdfContentItems(
        optimizeFooterBlocksForPdf(
          applyInvoiceTotalsPanelGeometry(applyPreviewGeometry(pruned)),
        ),
      ),
    );
  }
  if (typeof sanitized === "string" && sanitized.trim()) {
    return finalizePdfContentItems(
      prunePdfContentItems(
        optimizeFooterBlocksForPdf(
          applyInvoiceTotalsPanelGeometry(
            applyPreviewGeometry([{ text: sanitized }]),
          ),
        ),
      ),
    );
  }
  throw new Error("HTML to PDF conversion produced empty content.");
}

function keepSafeImages(images) {
  if (!images || typeof images !== "object") return {};
  const safe = {};
  Object.entries(images).forEach(([key, value]) => {
    if (typeof value === "string" && value.startsWith("data:")) {
      safe[key] = value;
    }
  });
  return safe;
}

/**
 * Build a vector PDF blob from an HTML document string.
 * @param {string} html
 */
export async function getPdfBlobFromHtmlString(html, options = {}) {
  if (!html?.trim()) {
    throw new Error("No HTML provided for vector PDF generation.");
  }

  const inlinedHtml = options.skipImageInlining
    ? html
    : await inlineImagesInHtml(html);
  const preparedHtml = prepareHtmlForPdfMake(inlinedHtml);

  let converted;
  try {
    converted = htmlToPdfmake(preparedHtml, {
      tableAutoSize: options.tableAutoSize === true,
      imagesByReference: true,
      removeExtraBlanks: true,
      window,
      defaultStyles: {
        th: { bold: true, fillColor: "#ebf7ed" },
        table: { margin: [0, 0, 0, 0] },
        div: { margin: [0, 0, 0, 0] },
        p: { margin: [0, 0, 0, 0] },
      },
    });
  } catch (error) {
    console.error("invoicePdfVector: html-to-pdfmake conversion failed", error);
    throw new Error("Unable to convert invoice HTML to PDF. Please try again.");
  }

  const { content, images } = normalizeHtmlToPdfmakeResult(converted);
  const safeImages = keepSafeImages(images);

  const documentDefinition = {
    pageSize: "A4",
    pageMargins: INVOICE_PAGE_MARGINS,
    defaultStyle: {
      font: "Roboto",
      fontSize: 13,
      color: "#111827",
      lineHeight: 1.5,
    },
    content: ensurePdfContent(content),
  };

  if (Object.keys(safeImages).length > 0) {
    documentDefinition.images = safeImages;
  }

  return pdfMakeGetBlob(documentDefinition);
}

/**
 * Finance / Xero-style invoice PDF from draft.
 * @param {object} draft
 */
export async function getPdfBlobFromInvoiceDraft(draft) {
  if (!draft) {
    throw new Error("Invoice draft is required for PDF export.");
  }

  const showLogo =
    draft?.previewDisplay?.invoiceDisplayOptions?.header?.showLogo !== false;
  let logoDataUrl = null;
  let previewLogoUrl = "";
  if (showLogo) {
    logoDataUrl = await resolveInvoicePdfLogoDataUrl(draft);
    previewLogoUrl =
      resolveInvoicePreviewLogoUrl({ invoiceSetting: draft?.invoiceSetting }) ||
      defaultBrandLogo;
  }

  const html = buildInvoiceEmailHtml(draft, {
    forPdf: true,
    ...(showLogo && logoDataUrl
      ? { logoDataUrl }
      : showLogo
        ? { logoUrl: previewLogoUrl }
        : {}),
  });
  if (!html?.trim()) {
    throw new Error("Invoice HTML could not be built for PDF export.");
  }

  try {
    return await getPdfBlobFromHtmlString(html);
  } catch (error) {
    console.error("invoicePdfVector: draft PDF failed", {
      invoiceNumber: draft.invoiceNumber || draft.invoice_number,
      previewDisplay: draft.previewDisplay,
      error,
    });
    throw error;
  }
}

/**
 * Application / legacy capture nodes (inline-styled DOM).
 * @param {HTMLElement} element
 */
export async function getPdfBlobFromInvoiceElement(element) {
  if (!element) {
    throw new Error("No element provided for invoice PDF export.");
  }

  const bodyHtml = element.outerHTML;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body>${bodyHtml}</body></html>`;

  try {
    return await getPdfBlobFromHtmlString(html);
  } catch (error) {
    console.error("invoicePdfVector: element PDF failed", error);
    throw error;
  }
}
